import { cn, ChevronDown, ChevronUp } from "@app/ui";
import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "../../../../lib/motion/index.js";
import { useI18n } from "../../../../lib/useI18n.js";
import type { MobileTopicDisplay } from "../../../../stores/settings.store.js";
import { useTopicRailModel, type TopicNode, type TopicRootNode } from "../../../topics/rail-model.js";
import { stickerGlyph } from "../../../task-form/paper/palette.js";
import { useHorizontalDragScroll } from "./useHorizontalDragScroll.js";

/** What the parent (sheet) needs to fire the drop animation for a selection. */
export interface TopicPick {
  id: string;
  color: string;
  rect: DOMRect;
}

interface Props {
  topicIds: string[];
  setTopicIds: (ids: string[]) => void;
  display: MobileTopicDisplay;
  multi: boolean;
  orientation?: "horizontal" | "vertical";
  className?: string;
  /** Fired when a topic/subtopic is newly selected, not on deselect. */
  onPick?: (pick: TopicPick) => void;
}

const colorOf = (n: TopicNode) => n.color;

/**
 * Mobile topic selector: a one-row, horizontally-scrolling field of segmented
 * topic buttons. Tapping a parent expands that same button into connected
 * subtopic sections that can be selected individually.
 */
export function TopicButtons({ topicIds, setTopicIds, display, multi, orientation = "horizontal", className, onPick }: Props) {
  const { t } = useI18n();
  const reduce = useReducedMotion();
  const roots = useTopicRailModel();
  const selected = useMemo(() => new Set(topicIds), [topicIds]);
  const [expandedRoot, setExpandedRoot] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const dragScroll = useHorizontalDragScroll(orientation === "vertical" ? "y" : "x");
  const { fadeStyle, ...dragScrollProps } = dragScroll;

  useEffect(() => {
    if (expandedRoot && !roots.some((r) => r.id === expandedRoot)) setExpandedRoot(null);
  }, [roots, expandedRoot]);

  const fire = (node: TopicNode, e: React.MouseEvent<HTMLButtonElement>) => {
    onPick?.({ id: node.id, color: colorOf(node), rect: e.currentTarget.getBoundingClientRect() });
  };

  const tapRoot = (root: typeof roots[number], e: React.MouseEvent<HTMLButtonElement>) => {
    const isOn = selected.has(root.id);
    if (isOn) {
      const drop = new Set([root.id, ...root.children.map((c) => c.id)]);
      setTopicIds(topicIds.filter((id) => !drop.has(id)));
      if (expandedRoot === root.id) setExpandedRoot(null);
      return;
    }
    if (multi) {
      setTopicIds([...topicIds, root.id]);
    } else {
      const drop = new Set<string>();
      roots.forEach((r) => { drop.add(r.id); r.children.forEach((c) => drop.add(c.id)); });
      setTopicIds([root.id, ...topicIds.filter((id) => !drop.has(id))]);
    }
    fire(root, e);
    if (root.children.length > 0) setExpandedRoot(root.id);
    else if (!multi) setCollapsed(true);
  };

  const tapSub = (sub: TopicNode, e: React.MouseEvent<HTMLButtonElement>) => {
    if (selected.has(sub.id)) {
      setTopicIds(topicIds.filter((id) => id !== sub.id));
    } else {
      setTopicIds([...topicIds, sub.id]);
      fire(sub, e);
    }
  };

  if (roots.length === 0) return null;

  const selectedRoots = roots.filter((r) => selected.has(r.id) || r.children.some((c) => selected.has(c.id)));

  if (collapsed) {
    return (
      <div className={cn("px-1", className)}>
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="flex w-full items-center gap-2 rounded-md border bg-background px-3 py-1.5 text-sm"
          aria-label={t("taskForm.chooseDifferentTopic")}
        >
          {selectedRoots.length > 0 ? (
            <span className="flex items-center gap-1.5 truncate">
              {selectedRoots.map((r) => (
                <span key={r.id} className="inline-flex items-center gap-1 truncate">
                  <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: colorOf(r) }} />
                  {r.icon ?? stickerGlyph(r.name)}
                </span>
              ))}
            </span>
          ) : (
            <span className="text-muted-foreground">{t("taskForm.topic")}</span>
          )}
          <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </div>
    );
  }

  return (
    <div className={cn("px-1", className)}>
      <div
        className={cn(
          "no-scrollbar cursor-grab gap-1.5 active:cursor-grabbing",
          orientation === "vertical"
            ? "flex h-full flex-col overflow-y-auto pr-1"
            : "-mx-4 grid grid-flow-col grid-rows-1 overflow-x-auto px-4 pb-1",
        )}
        style={{
          ...(orientation === "horizontal" ? { gridAutoColumns: "max-content" } : {}),
          touchAction: "none",
          scrollbarWidth: "none",
          ...fadeStyle,
        }}
        {...dragScrollProps}
      >
        <AnimatePresence initial={false}>
          {roots.map((root) => (
            <TopicGroup
              key={root.id}
              root={root}
              expanded={expandedRoot === root.id}
              selected={selected}
              display={display}
              orientation={orientation}
              reduce={!!reduce}
              onRoot={tapRoot}
              onSub={tapSub}
            />
          ))}
        </AnimatePresence>
      </div>
      {!multi && selectedRoots.length > 0 && (
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="mt-0.5 flex w-full items-center justify-center text-muted-foreground"
          aria-label={t("taskForm.collapseTopics")}
        >
          <ChevronUp className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function TopicGroup({
  root,
  expanded,
  selected,
  display,
  orientation,
  reduce,
  onRoot,
  onSub,
}: {
  root: TopicRootNode;
  expanded: boolean;
  selected: Set<string>;
  display: MobileTopicDisplay;
  orientation: "horizontal" | "vertical";
  reduce: boolean;
  onRoot: (root: TopicRootNode, e: React.MouseEvent<HTMLButtonElement>) => void;
  onSub: (sub: TopicNode, e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const rootOn = selected.has(root.id);
  const rootCol = colorOf(root);
  return (
    <motion.div
      layout={!reduce}
      initial={reduce ? false : { opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96 }}
      transition={reduce ? { duration: 0 } : { duration: 0.16 }}
      className={cn(
        "flex shrink-0 overflow-hidden border bg-background",
        orientation === "vertical" ? "w-full flex-col rounded-xl" : "rounded-full",
      )}
      style={{ borderColor: rootCol }}
    >
      <button
        type="button"
        onClick={(e) => onRoot(root, e)}
        className={cn(
          "flex min-w-0 items-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-sm transition-colors",
          orientation === "vertical" && "w-full",
          rootOn && "font-medium",
        )}
        style={rootOn ? { background: rootCol, color: readable(rootCol) } : undefined}
      >
        {display !== "text" && <span aria-hidden>{root.icon ?? stickerGlyph(root.name)}</span>}
        {display !== "icons" && <span className="min-w-0 truncate">{root.name}</span>}
      </button>
      <AnimatePresence initial={false}>
        {expanded && root.children.map((child) => {
          const on = selected.has(child.id);
          const col = colorOf(child);
          return (
            <motion.button
              key={child.id}
              type="button"
              layout={!reduce}
              initial={reduce ? false : { opacity: 0, ...(orientation === "vertical" ? { height: 0 } : { width: 0 }) }}
              animate={{ opacity: 1, ...(orientation === "vertical" ? { height: "auto" } : { width: "auto" }) }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, ...(orientation === "vertical" ? { height: 0 } : { width: 0 }) }}
              transition={reduce ? { duration: 0 } : { duration: 0.16 }}
              onClick={(e) => onSub(child, e)}
              className={cn(
                "flex min-w-0 items-center gap-1.5 whitespace-nowrap px-2.5 py-1 text-xs transition-colors",
                orientation === "vertical" ? "w-full border-t" : "border-l",
                on && "font-medium",
              )}
              style={on
                ? { background: col, borderColor: rootCol, color: readable(col) }
                : { borderColor: rootCol }}
            >
              {display !== "text" && <span aria-hidden>{child.icon ?? stickerGlyph(child.name)}</span>}
              {display !== "icons" && <span className="min-w-0 truncate">{child.name}</span>}
            </motion.button>
          );
        })}
      </AnimatePresence>
    </motion.div>
  );
}

/** Quick light/dark text pick for a solid button fill. */
function readable(color: string): string {
  const h = /^#?([0-9a-f]{6})$/i.exec(color.trim());
  if (!h) return "#1f2937";
  const n = parseInt(h[1]!, 16);
  const lum = 0.299 * ((n >> 16) & 0xff) + 0.587 * ((n >> 8) & 0xff) + 0.114 * (n & 0xff);
  return lum > 150 ? "#1f2937" : "#f8fafc";
}
